import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Chrome,
    Github,
    KeyRound,
    Mail,
    Monitor,
    ShieldCheck,
    Smartphone,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { API } from "@/lib/api";


const parseUA = (ua = "") => {
    const browser = ua.includes("Edg/")
        ? "Edge"
        : ua.includes("Chrome/")
          ? "Chrome"
          : ua.includes("Firefox/")
            ? "Firefox"
            : ua.includes("Safari/")
              ? "Safari"
              : "Browser";
    const os = /Windows/.test(ua)
        ? "Windows"
        : /Mac OS X/.test(ua)
          ? "macOS"
          : /Android/.test(ua)
            ? "Android"
            : /iPhone|iPad/.test(ua)
              ? "iOS"
              : /Linux/.test(ua)
                ? "Linux"
                : "device";
    const mobile = /Mobile|Android|iPhone|iPad/.test(ua);
    return { label: `${browser} on ${os}`, mobile };
};

const timeAgo = (iso) => {
    if (!iso) return "";
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

const ProviderIcon = ({ provider, className = "h-3.5 w-3.5" }) => {
    if (provider === "github") return <Github className={className} />;
    if (provider === "google") return <Chrome className={className} />;
    return <Mail className={className} />;
};

const eventLabel = (e) => {
    if (!e.success) return "Failed sign-in attempt";
    if (e.detail === "register") return "Account created";
    if (e.detail === "password changed") return "Password changed";
    return "Signed in";
};

export default function SecurityPanel() {
    const { user, logout } = useAuth();
    const [data, setData] = useState(null);
    const [revoking, setRevoking] = useState("");

    const load = useCallback(async () => {
        try {
            const r = await fetch(`${API}/auth/security`, { credentials: "include" });
            if (!r.ok) throw new Error("failed");
            setData(await r.json());
        } catch {
            toast.error("Could not load security activity");
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const revoke = async (sid) => {
        setRevoking(sid);
        try {
            const r = await fetch(`${API}/auth/security/sessions/${sid}`, {
                method: "DELETE",
                credentials: "include",
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "failed");
            toast.success("Session revoked");
            if (d.current) {
                await logout();
                return;
            }
            load();
        } catch (e) {
            toast.error("Could not revoke session", { description: String(e.message) });
        } finally {
            setRevoking("");
        }
    };

    const revokeOthers = async () => {
        setRevoking("others");
        try {
            const r = await fetch(`${API}/auth/security/sessions/revoke-others`, {
                method: "POST",
                credentials: "include",
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "failed");
            toast.success(
                d.revoked ? `Signed out ${d.revoked} other session${d.revoked > 1 ? "s" : ""}` : "No other sessions to sign out",
            );
            load();
        } catch (e) {
            toast.error("Could not sign out other sessions", { description: String(e.message) });
        } finally {
            setRevoking("");
        }
    };

    if (!data) {
        return (
            <div data-testid="security-loading" className="glass-panel rounded-xl p-6">
                <p className="font-mono text-[11px] text-slate-500">Loading security activity…</p>
            </div>
        );
    }

    const { sessions, events } = data;
    const others = sessions.filter((s) => !s.current);

    return (
        <div data-testid="security-panel" className="space-y-4">
            <div className="glass-panel rounded-xl p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                            Active sessions
                        </h3>
                        <p className="mt-1 font-mono text-[10px] text-slate-500">
                            {sessions.length} device{sessions.length === 1 ? "" : "s"} signed in ·
                            revoked sessions are cut off within 30 minutes
                        </p>
                    </div>
                    {others.length > 0 && (
                        <button
                            data-testid="revoke-others-button"
                            onClick={revokeOthers}
                            disabled={revoking === "others"}
                            className="rounded-md border border-rose-500/40 bg-rose-950/40 px-4 py-2 font-mono text-[11px] font-semibold text-rose-300 transition-colors hover:bg-rose-950/70 disabled:opacity-50"
                        >
                            {revoking === "others" ? "Signing out…" : "Sign out everywhere else"}
                        </button>
                    )}
                </div>

                <div data-testid="sessions-list" className="mt-5 space-y-2.5">
                    {sessions.length === 0 && (
                        <p className="font-mono text-[11px] text-slate-600">No active sessions found.</p>
                    )}
                    {sessions.map((s) => {
                        const dev = parseUA(s.user_agent);
                        const Icon = dev.mobile ? Smartphone : Monitor;
                        return (
                            <div
                                key={s.session_id}
                                data-testid={`session-item-${s.session_id}`}
                                className="flex items-center gap-3.5 rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-3"
                            >
                                <Icon className="h-4 w-4 shrink-0 text-slate-500" />
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs font-semibold text-slate-200">
                                            {dev.label}
                                        </span>
                                        {s.current && (
                                            <span
                                                data-testid="session-current-badge"
                                                className="rounded border border-cyan-500/30 bg-cyan-950/40 px-1.5 py-px font-mono text-[9px] uppercase tracking-wider text-cyan-300"
                                            >
                                                This device
                                            </span>
                                        )}
                                        <span className="rounded border border-slate-700 px-1.5 py-px font-mono text-[9px] uppercase tracking-wider text-slate-500">
                                            {s.kind === "google" ? "Google" : "Email / GitHub"}
                                        </span>
                                    </div>
                                    <p className="mt-1 font-mono text-[10px] text-slate-500">
                                        {s.ip || "unknown ip"} · signed in {timeAgo(s.created_at)}
                                    </p>
                                </div>
                                {!s.current && (
                                    <button
                                        data-testid={`session-revoke-${s.session_id}`}
                                        onClick={() => revoke(s.session_id)}
                                        disabled={revoking === s.session_id}
                                        className="shrink-0 rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-400 transition-colors hover:border-rose-500/50 hover:text-rose-300 disabled:opacity-50"
                                    >
                                        {revoking === s.session_id ? "…" : "Revoke"}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="glass-panel rounded-xl p-6">
                <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                    Recent sign-in activity
                </h3>
                <div data-testid="activity-list" className="mt-5 space-y-2.5">
                    {events.length === 0 && (
                        <p className="font-mono text-[11px] text-slate-600">No sign-in activity yet.</p>
                    )}
                    {events.map((e, i) => (
                        <div
                            key={`${e.at}-${i}`}
                            data-testid={`activity-item-${i}`}
                            className="flex items-center gap-3.5 rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-3"
                        >
                            <span
                                className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                                    e.success ? "bg-emerald-400" : "bg-rose-400"
                                }`}
                            />
                            <ProviderIcon provider={e.provider} className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                            <div className="min-w-0 flex-1">
                                <span
                                    className={`text-xs font-semibold ${
                                        e.success ? "text-slate-200" : "text-rose-300"
                                    }`}
                                >
                                    {eventLabel(e)}
                                </span>
                                <p className="mt-0.5 truncate font-mono text-[10px] text-slate-500">
                                    {parseUA(e.user_agent).label} · {e.ip || "unknown ip"}
                                </p>
                            </div>
                            <span className="shrink-0 font-mono text-[10px] text-slate-600">
                                {timeAgo(e.at)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="glass-panel rounded-xl p-6">
                <div className="flex items-start gap-3.5">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                    <div>
                        <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                            Don't recognize something?
                        </h3>
                        <p className="mt-1.5 text-xs leading-relaxed text-slate-400">
                            Sign out everywhere else above, then change your password. If you sign in
                            with Google or GitHub, secure that account instead.
                        </p>
                        {user?.auth_provider === "email" && (
                            <Link
                                to="/settings"
                                data-testid="security-change-password-link"
                                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-950/40 px-4 py-2 font-mono text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70"
                            >
                                <KeyRound className="h-3 w-3" /> Change password
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
